import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Company from "../api/Company";
import Brand from "../api/Brand";
import { toast } from "react-hot-toast";
import { emitCustomEvent } from "react-custom-events";
import Input from "../components/form/Input";
import Select from "../components/form/Select";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/organisms/Layout";

const AddBrand = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [brandName, setBrandName] = useState("");
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("-1");
  const [website, setWebsite] = useState("");
  const [validationErrors, setValidationErrors] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "HeyHire Portal-Add-New-Brand";
    loadCompanies();
  }, []);

  const loadCompanies = () => {
    Company.getAll().then((response) => {
      setCompanies(response.data.data);

      // Set the default selected company
      if (response.data.data.length > 0) {
        setSelectedCompany(response.data.data[0].id);
      }
    });
  };

  const addBrand = () => {
    setLoading(true);

    Brand.add({
      company_id: selectedCompany,
      name: brandName,
      url_slug: website,
      website: website,
    })
      .then(() => {
        toast.success(t("brands.brand-added"));
        emitCustomEvent("brandAdded");
        navigate("/companies/brands");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <Link to="/companies/brands" className="text-gray-500 hover:text-gray-700">
              <i className="far fa-chevron-left text-xl" />
            </Link>
            <h2 className="text-2xl font-bold text-gray-900">
              {t("brands.add-a-brand")}
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Fill in the information below to create a new brand.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white shadow-sm ring-1 ring-gray-300 rounded-xl p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addBrand();
            }}
            className="space-y-8"
          >
            {/* Brand Information Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                Brand Information
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Basic details about the brand.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <Input
                    darmo={true}
                    name="brand-name"
                    label={t("brands.brand-name")}
                    type="text"
                    value={brandName}
                    placeholder={t("brands.add-a-brand")}
                    error={
                      validationErrors && validationErrors.name
                        ? validationErrors.name[0]
                        : null
                    }
                    onChange={(e) => setBrandName(e.target.value)}
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    label={t("common.company")}
                    value={selectedCompany}
                    name="company"
                    onChange={(e) => {
                      setSelectedCompany(companies[e.target.selectedIndex].id);
                    }}
                  >
                    {companies.map((company) => {
                      return (
                        <option className="p-5" key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      );
                    })}
                  </Select>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Input
                    darmo={true}
                    name="url_slug"
                    label={t("common.website")}
                    type="text"
                    value={website}
                    slug="https://"
                    placeholder="www.example.com"
                    error={
                      validationErrors && validationErrors.url_slug
                        ? validationErrors.url_slug[0]
                        : null
                    }
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t border-gray-900/10 pt-8 flex justify-end gap-x-3">
              <button
                type="button"
                onClick={() => navigate("/companies/brands")}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {loading ? (
                  <i className="fad fa-circle-notch fa-spin" />
                ) : (
                  t("common.save")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddBrand;
